import { router, usePage } from "@inertiajs/react";
import { Trash2 } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RecordStepper from "@/components/shared/record-stepper";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { populateQueryParams } from "@/lib/utils";
import type { AppointmentIndexGlobalPageProps } from "@/pages/AdminPortal/Appointment/Index";
import type { Appointment } from "@/types/admin-portal/appointment";
import {
	type DeletableDocument,
	DeleteDocumentDialog,
	type DeleteDocumentTarget,
} from "../delete-document-dialog";
import AppointmentActionButtons, {
	DisabledReasonTooltip,
	deleteDocumentReason,
	useCanUpdateSoap,
} from "./action-buttons";
import AdminCard from "./admins";
import EvidenceCard from "./evidence-card";
import HistoryList from "./histories";
import AppointmentListItemDetails from "./index";
import PatientCard from "./patient";
import SoapCard from "./soap-card";
import TherapistCard from "./therapist";

export interface AppointmentDetailPanelProps {
	appointment: Appointment;
}

// Full appointment detail, rendered inside the dialog/drawer opened by ?view_appointment=<id>.
const AppointmentDetailPanel = memo(
	({ appointment }: AppointmentDetailPanelProps) => {
		const { t } = useTranslation("appointments");
		const { t: tGlobal } = useTranslation();
		const isMobile = useIsMobile();
		const canUpdateSoap = useCanUpdateSoap();
		const [deleting, setDeleting] = useState<DeleteDocumentTarget | null>(null);
		const { props: globalProps, url: pageURL } =
			usePage<AppointmentIndexGlobalPageProps>();

		// Swap the panel to a sibling visit without touching the table underneath.
		const showAppointment = useCallback(
			(id: string) => {
				const { fullUrl } = populateQueryParams(pageURL, {
					view_appointment: id,
				});
				router.get(
					fullUrl,
					{},
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
					},
				);
			},
			[pageURL],
		);

		// The deferred `appointments` prop survives the partial reload the stepper
		// issues, so the table's row order stays put while only the panel swaps.
		const rowIds = useMemo(
			() =>
				(globalProps?.appointments?.data ?? []).flatMap((group) =>
					group.schedules.map((schedule) => String(schedule.id)),
				),
			[globalProps?.appointments],
		);

		return (
			<div className="flex flex-col gap-6">
				<RecordStepper
					ids={rowIds}
					currentId={String(appointment.id)}
					param="view_appointment"
					only={["selectedAppointment"]}
					prevLabel={t("modal.view_appointment.prev")}
					nextLabel={t("modal.view_appointment.next")}
				/>

				{/* Actions lead the panel at every width — the content below runs long
				    enough that burying them at the end costs a scroll. Kept first in the
				    DOM as well as first on screen, so focus order matches. */}
				<AppointmentActionButtons
					schedule={appointment}
					isExpanded={true}
					buttonSize="sm"
					className="pb-5 border-b border-border"
				/>

				<div className="flex flex-col gap-3">
					<h3 className="text-xs font-light uppercase">
						{t("list.appointment_details")}
					</h3>
					<AppointmentListItemDetails
						schedule={appointment}
						isFreshAppt={false}
						isMobile={isMobile}
						seeDetailVisitSeries={showAppointment}
					/>
				</div>

				<div className="flex flex-col gap-3">
					<h3 className="text-xs font-light uppercase">
						{t("list.status_histories.title")}
					</h3>
					<div className="flex flex-col gap-4 p-4 border rounded-lg shadow-inner border-border bg-sidebar">
						<HistoryList histories={appointment.statusHistories} />
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
					<div className="flex flex-col gap-3">
						<h3 className="text-xs font-light uppercase">
							{t("list.patient_details")}
						</h3>
						<PatientCard
							patient={appointment.patient}
							patientMedicalRecord={appointment.patientMedicalRecord}
							className="flex flex-col h-full"
						/>
					</div>
					<div className="flex flex-col gap-3">
						<h3 className="text-xs font-light uppercase">
							{t("list.therapist_details")}
						</h3>
						<TherapistCard
							therapist={appointment.therapist}
							className="flex flex-col h-full "
						/>
					</div>
					<div className="flex flex-col gap-3">
						<h3 className="text-xs font-light uppercase">
							{t("list.pic_details")}
						</h3>
						<AdminCard
							admins={appointment.admins}
							className="flex flex-col h-full"
						/>
					</div>
				</div>

				{/* SOAP and Evidence Section. Each heading carries its delete button, so
				    the admin can remove a document while looking at it. */}
				{(
					[
						{
							kind: "soap",
							title: "SOAP Notes",
							label: t("button.delete_soap"),
							body: appointment.soap && <SoapCard soap={appointment.soap} />,
						},
						{
							kind: "evidence",
							title: "Digital Evidence",
							label: t("button.delete_evidence"),
							body: appointment.evidence && (
								<EvidenceCard evidence={appointment.evidence} />
							),
						},
					] satisfies {
						kind: DeletableDocument;
						title: string;
						label: string;
						body: React.ReactNode;
					}[]
				).map(
					({ kind, title, label, body }) =>
						body && (
							<div key={kind} className="flex flex-col gap-3">
								<div className="flex items-center justify-between gap-2">
									<h3 className="text-xs font-light uppercase">{title}</h3>
									<DisabledReasonTooltip
										// Only rendered when the document exists, so the whitelist is the one reason left.
										reason={deleteDocumentReason(kind, true, canUpdateSoap)}
										className="w-fit"
									>
										<Button
											variant="ghost-destructive"
											size="sm"
											disabled={!canUpdateSoap}
											onClick={() =>
												setDeleting({
													appointmentId: String(appointment.id),
													kind,
													visitNumber: appointment.visitNumber,
													totalVisits: appointment.totalPackageVisits,
													therapistName: appointment.therapist?.name,
													hasOtherDocument:
														kind === "soap"
															? !!appointment.evidence
															: !!appointment.soap,
												})
											}
										>
											<Trash2 aria-hidden="true" />
											{label}
										</Button>
									</DisabledReasonTooltip>
								</div>
								{body}
							</div>
						),
				)}

				<DeleteDocumentDialog
					target={deleting}
					onClose={() => setDeleting(null)}
					reloadOnly={[
						"adminPortal",
						"flash",
						"errors",
						"selectedAppointment",
						"appointments",
					]}
				/>

				{/* The drawer pins its own close button in the footer; the dialog only has
				    the corner ✕, so this bar stands in for it — sticky to the bottom of the
				    scrollport so it is reachable without scrolling to the end. Shown from
				    md because that is where ResponsiveDialog switches to the dialog. */}
				<div className="sticky bottom-0 z-10 hidden pt-3 pb-1 -mb-1 border-t border-border bg-background md:flex md:justify-end">
					<DialogClose asChild>
						<Button variant="outline" size="sm" className="shadow-none">
							{tGlobal("components.modal.cancel")}
						</Button>
					</DialogClose>
				</div>
			</div>
		);
	},
);

export default AppointmentDetailPanel;
